// src/Pages/PreOrder/Camera/hooks/useFaceValidation.js
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { FACE_DETECTION_CONFIG } from "../../FaceValidation/constants";

const useFaceValidation = (videoRef, config = {}) => {
  // Merge user config with defaults - wrapped in useMemo to prevent recreation
  const validationConfig = useMemo(
    () => ({
      minRatio: config.minRatio || FACE_DETECTION_CONFIG.ratio.min,
      maxRatio: config.maxRatio || FACE_DETECTION_CONFIG.ratio.max,
      centerThreshold:
        config.centerThreshold ||
        FACE_DETECTION_CONFIG.position.centerThreshold,
      edgeBuffer:
        config.edgeBuffer || FACE_DETECTION_CONFIG.position.edgeBuffer,
      messages: config.messages || FACE_DETECTION_CONFIG.cameraMessages,
    }),
    [
      config.minRatio,
      config.maxRatio,
      config.centerThreshold,
      config.edgeBuffer,
      config.messages,
    ]
  );

  // Simple states
  const [isValid, setIsValid] = useState(false);
  const [message, setMessage] = useState("Loading...");
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [currentFaceData, setCurrentFaceData] = useState(null);

  // Canvas for face detection visualization
  const canvasRef = useRef(null);
  const detectionIntervalRef = useRef(null);

  // Load face-api models once
  useEffect(() => {
    const loadModels = async () => {
      try {
        if (!window.faceapi) {
          throw new Error("face-api.js not loaded");
        }

        // Only load if not already loaded
        if (!window.faceapi.nets.tinyFaceDetector.isLoaded) {
          const MODEL_URL = `${process.env.PUBLIC_URL || ""}/models`;
          await Promise.all([
            window.faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
            window.faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
          ]);
        }

        setIsModelLoaded(true);
        setMessage("Looking for face...");
      } catch (err) {
        console.error("Error loading face detection models:", err);
        setMessage("Error loading face detection");
      }
    };

    loadModels();

    // Cleanup interval on unmount
    return () => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
      }
    };
  }, []);

  // Validate single face detection result
  const validateFacePosition = useCallback(
    (face, videoWidth, videoHeight) => {
      const { box } = face.detection; // Calculate face center coordinates
      const centerX = box.x + box.width / 2;
      const centerY = box.y + box.height / 2;

      // Calculate video center coordinates
      const videoCenterX = videoWidth / 2;
      const videoCenterY = videoHeight / 2;

      // Check centering - are the faces close enough to center
      const centerDistanceX = Math.abs(centerX - videoCenterX);
      const centerDistanceY = Math.abs(centerY - videoCenterY);

      const isCentered =
        centerDistanceX < videoWidth * validationConfig.centerThreshold &&
        centerDistanceY < videoHeight * validationConfig.centerThreshold; // Check edges - are the faces far enough from the borders
      const isInFrame =
        box.x > validationConfig.edgeBuffer &&
        box.y > validationConfig.edgeBuffer &&
        box.x + box.width < videoWidth - validationConfig.edgeBuffer &&
        box.y + box.height < videoHeight - validationConfig.edgeBuffer;

      // Calculate face size ratio
      const ratio = box.height / videoHeight;

      // Check all conditions
      if (!isCentered) {
        return {
          valid: false,
          message: validationConfig.messages.notCentered,
          details: { centered: false, inFrame: isInFrame, ratio },
        };
      }

      if (!isInFrame) {
        return {
          valid: false,
          message: validationConfig.messages.notCentered, // Or specific message for edges
          details: { centered: isCentered, inFrame: false, ratio },
        };
      }

      if (ratio < validationConfig.minRatio) {
        return {
          valid: false,
          message: validationConfig.messages.tooFar,
          details: { centered: isCentered, inFrame: isInFrame, ratio },
        };
      }

      if (ratio > validationConfig.maxRatio) {
        return {
          valid: false,
          message: validationConfig.messages.tooNear,
          details: { centered: isCentered, inFrame: isInFrame, ratio },
        };
      } // All conditions met!
      return {
        valid: true,
        message: validationConfig.messages.good,
        details: { centered: isCentered, inFrame: isInFrame, ratio },
      };
    },
    [validationConfig]
  );

  // Main face detection loop
  const runDetection = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || !isModelLoaded) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Skip if video not ready
    if (video.videoWidth === 0 || video.videoHeight === 0) return;

    try {
      // Set canvas dimensions to match video
      if (
        canvas.width !== video.videoWidth ||
        canvas.height !== video.videoHeight
      ) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
      }

      // Detect faces
      const detections = await window.faceapi
        .detectAllFaces(
          video,
          new window.faceapi.TinyFaceDetectorOptions({
            inputSize: 224, // Reduced from 320 to 224 for better performance
            scoreThreshold: 0.4, // Reduced from 0.5 to 0.4
          })
        )
        .withFaceLandmarks();

      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Handle detection results
      if (detections.length === 0) {
        setIsValid(false);
        setMessage(validationConfig.messages.noFace);
        setCurrentFaceData(null);
      } else if (detections.length > 1) {
        setIsValid(false);
        setMessage(validationConfig.messages.multipleFaces);
        setCurrentFaceData(null);
      } else {
        // Single face detected - validate position and size
        const face = window.faceapi.resizeResults(detections[0], {
          width: video.videoWidth,
          height: video.videoHeight,
        });

        const validation = validateFacePosition(
          face,
          video.videoWidth,
          video.videoHeight
        );

        setIsValid(validation.valid);
        setMessage(validation.message);

        // Update current face data for visual guide
        const ratio = face.detection.box.height / video.videoHeight;
        setCurrentFaceData({
          box: face.detection.box,
          ratio: ratio,
          centered: validation.details?.centered || false,
          inFrame: validation.details?.inFrame || false,
        });

        // Draw detection box with different colors based on validation
        const { box } = face.detection; // Choose color by status
        if (validation.valid) {
          ctx.strokeStyle = "rgba(46, 204, 113, 0.8)"; // Green
          ctx.lineWidth = 3;
        } else if (
          !validation.details?.centered ||
          !validation.details?.inFrame
        ) {
          ctx.strokeStyle = "rgba(52, 152, 219, 0.8)"; // Blue - not centered
          ctx.lineWidth = 2;
        } else {
          ctx.strokeStyle = "rgba(231, 76, 60, 0.6)"; // Red - incorrect size
          ctx.lineWidth = 2;
        }

        ctx.strokeRect(box.x, box.y, box.width, box.height);

        // Draw centering guide lines (optional)
        if (!validation.details?.centered) {
          ctx.strokeStyle = "rgba(52, 152, 219, 0.3)";
          ctx.lineWidth = 1;
          ctx.setLineDash([5, 5]);

          // Vertical center line
          ctx.beginPath();
          ctx.moveTo(video.videoWidth / 2, 0);
          ctx.lineTo(video.videoWidth / 2, video.videoHeight);
          ctx.stroke(); // Horizontal center line
          ctx.beginPath();
          ctx.moveTo(0, video.videoHeight / 2);
          ctx.lineTo(video.videoWidth, video.videoHeight / 2);
          ctx.stroke();

          ctx.setLineDash([]); // Reset dashed line
        }
      }
    } catch (err) {
      console.error("Face detection error:", err);
      setIsValid(false);
      setMessage("Detection error");
    }
  }, [
    videoRef,
    isModelLoaded,
    validateFacePosition,
    validationConfig.messages,
  ]);

  // Start/stop detection based on video readiness
  useEffect(() => {
    if (!videoRef.current || !isModelLoaded) return;

    const video = videoRef.current;

    const startDetection = () => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
      }

      // Run detection every 300ms for smooth real-time feedback
      detectionIntervalRef.current = setInterval(runDetection, 150);
    };

    const stopDetection = () => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
        detectionIntervalRef.current = null;
      }
    };

    // Start when video is playing
    video.addEventListener("playing", startDetection);
    video.addEventListener("pause", stopDetection);
    video.addEventListener("ended", stopDetection);

    // Start immediately if video is already playing
    if (!video.paused && !video.ended && video.readyState > 2) {
      startDetection();
    }

    return () => {
      video.removeEventListener("playing", startDetection);
      video.removeEventListener("pause", stopDetection);
      video.removeEventListener("ended", stopDetection);
      stopDetection();
    };
  }, [videoRef, isModelLoaded, runDetection]);

  return {
    // Validation results
    isValid,
    message,
    isModelLoaded,
    currentFaceData,

    // Canvas for visualization
    canvasRef,

    // Manual control (if needed)
    runDetection,
  };
};

export default useFaceValidation;
